export type WakeLockState = "active" | "inactive" | "unsupported" | "failed";

export class ScreenWakeLock {
  private sentinel: WakeLockSentinel | null = null;
  private desired = false;
  private readonly visibilityHandler = () => {
    if (this.desired && document.visibilityState === "visible") void this.acquire();
  };

  constructor(private readonly onStateChange: (state: WakeLockState) => void) {
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  async enable(): Promise<void> {
    this.desired = true;
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.desired = false;
    await this.sentinel?.release();
    this.sentinel = null;
    this.onStateChange("inactive");
  }

  async toggle(): Promise<void> {
    if (this.desired && this.sentinel) await this.disable();
    else await this.enable();
  }

  async stop(): Promise<void> {
    this.desired = false;
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    await this.sentinel?.release();
    this.sentinel = null;
  }

  private async acquire(): Promise<void> {
    if (!("wakeLock" in navigator)) {
      this.onStateChange("unsupported");
      return;
    }
    if (!this.desired || document.visibilityState !== "visible" || this.sentinel) return;
    try {
      const sentinel = await navigator.wakeLock.request("screen");
      this.sentinel = sentinel;
      sentinel.addEventListener("release", () => {
        if (this.sentinel === sentinel) this.sentinel = null;
        if (this.desired && document.visibilityState === "visible") this.onStateChange("failed");
      });
      this.onStateChange("active");
    } catch {
      this.sentinel = null;
      this.onStateChange("failed");
    }
  }
}
